import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/Header';
import Home from './components/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import TermsConditions from './pages/TermsConditions';

function RewriteApp() {
    const [message, setMessage] = useState('');
    const [selectedTone, setSelectedTone] = useState('professional');
    const [rewrittenMessage, setRewrittenMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);


    const rewriteMessage = useCallback((originalMessage, tone) => {
        let rewritten = originalMessage;

        return rewritten;
    }, []);


    const handleRewrite = useCallback(async () => {
        if (!message.trim() || isProcessing) return;

        setIsProcessing(true);

        try {
            // Map selectedTone (e.g., 'professional') to API expected format 'Professional'
            const apiTone = selectedTone && selectedTone.length
                ? selectedTone.charAt(0).toUpperCase() + selectedTone.slice(1)
                : 'Professional';

            const resp = await fetch('https://rewrite.saisajan.workers.dev/rewrite', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'authorization': 'Bearer eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiYWRtaW4iOnRydWUsImlhdCI6MTUxNjIzOTAyMn0'
                },
                body: JSON.stringify({ text: message, tone: apiTone })
            });

            if (!resp.ok) {
                // non-2xx status
                throw new Error(`Network response was not ok (${resp.status})`);
            }

            const json = await resp.json();

            if (json && json.success && json.data && json.data.response) {
                setRewrittenMessage(json.data.response);
            } else if (json && json.data && json.data.response) {
                // tolerate responses that don't set success flag
                setRewrittenMessage(json.data.response);
            } else {
                // fallback to local rewrite if API returns unexpected shape
                console.warn('Unexpected API response shape, falling back to local rewrite', json);
                const fallback = rewriteMessage(message, selectedTone);
                setRewrittenMessage(fallback);
            }
        } catch (err) {
            console.error('Failed to call rewrite API:', err);
            // Fallback to local rewrite logic so the feature still works offline
            setRewrittenMessage('Failed to rewrite message. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    }, [message, isProcessing, selectedTone, rewriteMessage]);

    const handleCopy = async () => {
        if (!rewrittenMessage) return;

        try {
            await navigator.clipboard.writeText(rewrittenMessage);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = rewrittenMessage;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                handleRewrite();
            }

            if (event.key === 'Escape' && message) {
                setMessage('');
                setRewrittenMessage('');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [message, handleRewrite]);

    return (
        <Router>
            <ThemeProvider>
                <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-950">
                <Header />

                {/* Main Content */}
                <div className="flex flex-1">
                    {/* Editor Area */}
                    <div className="flex-1 flex flex-col p-3 sm:p-4 lg:p-6">
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <Home
                                        message={message}
                                        setMessage={setMessage}
                                        selectedTone={selectedTone}
                                        setSelectedTone={setSelectedTone}
                                        onRewrite={handleRewrite}
                                        rewrittenMessage={rewrittenMessage}
                                        onCopy={handleCopy}
                                        copySuccess={copySuccess}
                                        isProcessing={isProcessing}
                                    />
                                }
                            />
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            <Route path="/about" element={<AboutUs />} />
                            <Route path="/contact" element={<ContactUs />} />
                            <Route path="/terms" element={<TermsConditions />} />
                        </Routes>
                    </div>
                </div>

                {/* Footer */}
                <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-4 sm:px-6 py-4 mt-auto">
                    <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <span className="text-gray-600 dark:text-gray-400 text-sm">© 2025 RewriteKing</span>
                            <div className="flex items-center gap-4 flex-wrap justify-center">
                                <Link to="/privacy" className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm">Privacy</Link>
                                <Link to="/terms" className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm">Terms</Link>
                                <Link to="/contact" className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm">Contact</Link>
                                <Link to="/about" className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm">About</Link>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <a href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                                </svg>
                            </a>
                            <a href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </footer>
                </div>
            </ThemeProvider>
        </Router>
    );
}

function App() {
    return <RewriteApp />;
}

export default App;